import { Injectable } from '@angular/core';
import { SalesDocumentPosition } from '../models/sales-document-position.model';
import { SalesDocument } from '../models/sales-document.model';
import { core, deepCompare, equalNumbers, isArray, isEmptyObject, isNumber, isObject, isString, pricesRevisionsToString } from '@icc/shared/helpers/b2b';
import { parseDate } from '@icc/shared/helpers';

export type DocumentDifference = {
    prop: string;
    value1: any;
    value2: any;
};

interface DocumentPropertyCompare<T extends keyof SalesDocument> {
    prop: T;
    check?: (value1: SalesDocument[T], value2: SalesDocument[T]) => boolean;
}

interface DocumentPositionPropertyCompare<T extends keyof SalesDocumentPosition> {
    prop: T;
    check?: (value1: SalesDocumentPosition[T], value2: SalesDocumentPosition[T]) => boolean;
}

@Injectable({
    providedIn: 'root',
})
export class DocumentIntegrityService {
    constructor() {}

    static checkDocumentsIntegrity(document1: SalesDocument, document2: SalesDocument) {
        const diff: DocumentDifference[] = [];
        const propertyIdentical: DocumentPropertyCompare<keyof SalesDocument>[] = [
            {
                prop: 'ignorePriceVersionValidity',
                check: (value1, value2) => (!value1 && !value2) || value1 === value2,
            },
            // {
            //     prop: 'requestedDeadline',
            //     check: (value1, value2) => (!value1 && !value2) || parseDate(value1)?.getTime() === parseDate(value2)?.getTime(),
            // },
            {
                prop: 'dimensionUnits',
                check: (value1, value2) => (!value1 && !value2) || value1 === value2,
            },
            {
                prop: 'description',
                check: (value1, value2) => (!value1 && !value2) || value1 === value2,
            },
        ];
        for (const el of propertyIdentical) {
            if (el.check) {
                if (!el.check(document1[el.prop], document2[el.prop])) {
                    diff.push({
                        prop: el.prop,
                        value1: document1[el.prop],
                        value2: document2[el.prop],
                    });
                }
            } else if (!deepCompare(document1[el.prop], document2[el.prop])) {
                diff.push({
                    prop: el.prop,
                    value1: document1[el.prop],
                    value2: document2[el.prop],
                });
            }
        }
        return diff;
    }

    static checkPositionsIntegrity(salesPosition: SalesDocumentPosition, buyPosition: SalesDocumentPosition, buyPositionsMap: Map<string, SalesDocumentPosition>,) {
        const diff: DocumentDifference[] = [];
        if (!salesPosition || !buyPosition) {
            diff.push({
                prop: 'position',
                value1: salesPosition,
                value2: buyPosition,
            });
            return diff;
        }
        const propertyIdentical: DocumentPositionPropertyCompare<keyof SalesDocumentPosition>[] = [
            {
                prop: 'configuration',
                check: (value1, value2) => {
                    const theSameOptions =  deepCompare(value1, value2, new Set([
                        'priceParts',
                        'pricePartsNoMargin',
                        'priceSegments',
                        'priceSegmentsNoMargin',
                        'price',
                        'priceNoMargin',
                        'priceAfterMultiplier',
                        'priceAfterDiscounts',
                        'priceIsGross',
                        'priceTaxRate',
                        'PriceParts',
                        'PricePartsNoMargin',
                        'PriceSegments',
                        'PriceSegmentsNoMargin',
                        'Price',
                        'PriceNoMargin',
                        'PriceAfterMultiplier',
                        'PriceAfterDiscounts',
                        'PriceIsGross',
                        'PriceTaxRate',
                        'quantity',
                        'Quantity',
                        'PriceElemsNoMargin',
                        'PriceElems',
                        'NoPriceElems',
                        'NoPriceCauses',
                        'PriceValid',
                        'PriceAfterMultiplierNoMargin',
                        'priceAfterMultiplierNoMargin',
                        'unit',
                        'pricesRevisions',
                        'PricesRevisions',
                    ]));
                    if (!theSameOptions) {
                        return false;
                    }
                    if (buyPosition.customPriceValue) {
                        return deepCompare(value1.priceSegmentsNoMargin, value2.priceSegments);
                    }
                    return theSameOptions;
                }
            },
            {
                prop: 'photos',
                check: (value1, value2) =>
                    (!value1 && !value2) ||
                    (!value1 && isEmptyObject(value2)) ||
                    (!value2 && isEmptyObject(value1)) ||
                    deepCompare(value1, value2),
            },
            {
                prop: 'name',
                check: (value1, value2) => (!value1 && !value2) || value1 === value2,
            },
            {
                prop: 'description',
                check: (value1, value2) => (!value1 && !value2) || value1 === value2,
            },
            {
                prop: 'quantity',
                check: (value1, value2) => value1 == value2,
            },
            {
                prop: 'priceUnitId',
                check: (value1, value2) => value1 == value2,
            },
            {
                prop: 'department',
                check: (value1, value2) =>
                    (!value1 && !value2) ||
                    (!value1 && isEmptyObject(value2)) ||
                    (!value2 && isEmptyObject(value1)) ||
                    deepCompare(value1, value2),
            },
            {
                prop: 'basePriceValue',
                check: (value1, value2) => salesPosition.baseBuyPriceValue == null && value2 == null ||
                    equalNumbers(salesPosition.baseBuyPriceValue, value2)
                        && salesPosition.baseBuyPriceIsGross === buyPosition.basePriceIsGross,
            },
            {
                prop: 'externalSource',
                check: (value1, value2) => value1 == value2,
            },
            {
                prop: 'customPriceValue',
                check: (value1, value2) => (!value1 && !value2) || equalNumbers(value1, value2),
            },
            {
                prop: 'isDeleted',
                check: (value1, value2) => (!value1 && !value2) || value1 === value2,
            },
            {
                prop: 'masters',
                check: (value1: SalesDocumentPosition['masters'], value2: SalesDocumentPosition['masters']) => {
                    const value1Keys = Object.keys(value1 || {}).length;
                    const value2Keys = Object.keys(value2 || {}).length;
                    if (value1Keys && value2Keys) {
                        for (const buyPositionId in value2) {
                            if (value2[buyPositionId]) {
                                const salesPositionId = Array.from(buyPositionsMap.entries()).find(([key, value]) => value.uuid === buyPositionId)?.[0];
                                if (!value1[salesPositionId]) {
                                    return false;
                                }
                                for (const fieldId in value2[buyPositionId]) {
                                    if (value1[salesPositionId][fieldId] !== value2[buyPositionId][fieldId]) {
                                        return false;
                                    }
                                }
                                for (const fieldId in value1[salesPositionId]) {
                                    if (value1[salesPositionId][fieldId] !== value2[buyPositionId][fieldId]) {
                                        return false;
                                    }
                                }
                            }
                        }
                    }
                    if (!value1Keys && value2Keys) {
                        return false;
                    }
                    return true;
                },
            },
            {
                prop: 'slaves',
                check: (value1: SalesDocumentPosition['slaves'], value2: SalesDocumentPosition['slaves']) => {
                    for (const field in value1) {
                        if (value1[field] && value2[field]) {
                            const salesPositionId = Array.from(buyPositionsMap.entries()).find(([key, value]) => value?.uuid === value2?.[field]?.positionId)?.[0];
                            if (!salesPositionId) {
                                return false;
                            }
                            if (salesPositionId !== value1[field].positionId) {
                                return false;
                            }
                            if (value2[field].productId !== value1[field].productId) {
                                return false;
                            }
                            if (value2[field].unitId !== value1[field].unitId || value2[field].unitCode !== value1[field].unitCode) {
                                return false;
                            }
                        }
                    }
                    for (const field in value2) {
                        if (!value1[field]) {
                            return false;
                        }
                    }
                    return true;
                }
            }
        ];
        for (const el of propertyIdentical) {
            if (el.check) {
                if (!el.check(salesPosition[el.prop], buyPosition[el.prop])) {
                    diff.push({
                        prop: el.prop,
                        value1: salesPosition[el.prop],
                        value2: buyPosition[el.prop],
                    });
                }
            } else if (!deepCompare(salesPosition[el.prop], buyPosition[el.prop])) {
                diff.push({
                    prop: el.prop,
                    value1: salesPosition[el.prop],
                    value2: buyPosition[el.prop],
                });
            }
        }
        return diff;
    }
}
